/**
 * Utility functions to check if evaluation cards have data to display
 *
 * Each function takes an evaluation object and returns a boolean indicating
 * whether that specific card has meaningful data to show to the user.
 *
 * These functions are used by parent components to conditionally render cards
 * when the "Display non-empty cards only" toggle is enabled.
 *
 * @param {Evaluation} evaluation - The evaluation response to check
 * @returns {boolean} True if the current card has data; false if it has no data
 */

import type { Evaluation } from '~/entities/types';

/**
 * Check if Returned Reason Codes card has data
 */
export const hasReturnedReasonCodesData = (evaluation: Evaluation): boolean => {
  const taggedResults = evaluation.tagged_reason_code_results || {};
  const untaggedResults = evaluation.untagged_reason_code_results || {};
  const extraneousResults = evaluation.extraneous_reason_code_results || {};

  return (
    Object.keys(taggedResults).length > 0
    || Object.keys(untaggedResults).length > 0
    || Object.keys(extraneousResults).length > 0
  );
};

/**
 * Check if Old Returned Reason Codes card has data (pre-cutoff evaluations)
 */
export const hasOldReturnedReasonCodesData = (evaluation: Evaluation): boolean => {
  const reasonCodes = evaluation.reason_codes_results || {};
  const extraneousCodes = evaluation.extraneous_reason_codes || {};

  return Object.keys(reasonCodes).length > 0 || Object.keys(extraneousCodes).length > 0;
};

/**
 * Check if Data Sources card has data
 */
export const hasDataSourcesData = (evaluation: Evaluation): boolean => !!(
  evaluation.final_response?.summary?.services
    && Object.keys(evaluation.final_response.summary.services).length > 0
);

/**
 * Check if Fraud Risk card should display.
 *
 * Requirements:
 * - alloy_fraud_score is not null
 * - alloy_fraud_score_response exists
 * - model_control_number >= 78 (threshold for newer fraud models)
 * - Either fraud_score or feature_importance data exists
 */
const FRAUD_MODEL_CONTROL_NUMBER_THRESHOLD = 78;

export const hasFraudRiskData = (evaluation: Evaluation): boolean => {
  const fraudScore = evaluation.alloy_fraud_score;
  const fraudResponse = evaluation.alloy_fraud_score_response;

  if (fraudScore === null || !fraudResponse) {
    return false;
  }

  const modelControlNumber = parseFloat(fraudResponse.model_control_number);
  const meetsThreshold = modelControlNumber >= FRAUD_MODEL_CONTROL_NUMBER_THRESHOLD;
  const hasData = fraudResponse.fraud_score != null || !!fraudResponse.feature_importance;

  return meetsThreshold && hasData;
};

/**
 * Check if Found Data card has data
 */
export const hasFoundData = (evaluation: Evaluation): boolean => {
  const foundData = evaluation.final_response?.found_data;
  return !!(foundData && Object.keys(foundData).length > 0);
};

/**
 * Check if Entity Notes card has data
 */
export const hasEntityNotes = (entityNotes: any): boolean => !!(
  entityNotes && Array.isArray(entityNotes) && entityNotes.length > 0);

/**
 * Check if Validation Errors card should show
 */
export const hasValidationErrors = (evaluation: Evaluation): boolean => !!(
  evaluation.required && Object.keys(evaluation.required).length > 0);

/**
 * List of watchlist vendor service names.
 *
 * Note: Socure and GIACT have module configurations for watchlist, but if either
 * service is configured in the workflow, assume watchlist modules are enabled.
 * Note: sentilinkv2applicationrisk is explicitly excluded (v1 only).
 */
const WATCHLIST_VENDOR_NAMES = [
  'Baselayer Business',
  'CLEAR Verified',
  'ComplyAdvantage',
  'GIACT Verification Services',
  'Lexis Nexis World Compliance',
  'Lexis Nexis Instant ID',
  'LSEG World-Check One',
  'Middesk',
  "Moody's Grid",
  'Quantifind',
  'Plaid Monitor',
  'Sentilink',
  'Socure 30',
  'ComplyAdvantage Mesh Person',
  'ComplyAdvantage Mesh Business',
] as const;

/**
 * Check if any watchlist vendor service is configured in the workflow.
 * Used to conditionally render Watchlist Matches card/tab.
 */
export const hasWatchlistServiceConfigured = (evaluation: Evaluation): boolean => {
  const services = evaluation.final_response?.summary?.services;
  if (!services) return false;

  const configuredServices = Object.keys(services);
  return WATCHLIST_VENDOR_NAMES.some(vendor => configuredServices.includes(vendor));
};

/**
 * List of credit vendor service names.
 * Used to determine if the Credit Panel should be rendered.
 */
/**
 * List of credit vendor service names (alphabetical order).
 * Used to determine if the Credit Panel should be rendered.
 *
 * Includes services from two product categories:
 * 1. Traditional Credit Data - Bureau credit reports and scores
 * 2. Business Identity & Credit Data - Business credit and verification
 *
 * Note: Alt Credit Data vendors (e.g., LexisNexis RiskView, Nova Credit,
 * Plaid Assets, Codat, Pave) are intentionally excluded.
 */
const CREDIT_VENDOR_NAMES = [
  // Core Credit Panel services (have EntityCreditJqSelectors in Chimera)
  'BizChex',
  'Creditor Watch Custom Profile',
  'Equifax',
  'Equifax OneView Consumer Credit Report',
  'EquifaxBusinessCreditIndustryReport',
  'Experian',
  'Experian Consumer Credit Profile',
  'QualiFile',
  'TransUnion Credit',
  // Additional Equifax products (Traditional Credit Data)
  'Equifax AU Apply',
  'Equifax AU Business Trading History',
  'Equifax AU IDMatrix',
  'Equifax AU Indepth Trading History',
  'Equifax Canada',
  'Equifax Limited Consumer Report',
  'Equifax NZ',
  'Equifax Spain',
  'Equifax UK Business Search',
  'Equifax UK Gateway',
  'EquifaxCanadaEID',
  'EquifaxTWN',
  'Equifax XML',
  // Experian products (Traditional Credit Data)
  'Experian Business Credit',
  'Experian Business Profile',
  'Experian CrossCore FraudNet',
  'Experian SBFE Multisegment',
  'Experian UK Crosscore Confirmation of Payee and Bank Account Verification - Business',
  'Experian UK Crosscore Confirmation of Payee and Bank Account Verification - Person',
  'Experian UK Crosscore Identity Services KYC',
  'Experian UK Delphi Select Single Applicant',
  'Experian UK KYB Data Company',
  // TransUnion products (Traditional Credit Data)
  'TransUnion Canada Model Report',
  'TransUnion Model Report',
  'Transunion UK Affordability Report',
  'Transunion UK TrueVision',
  // Condukt products (Business Identity & Credit Data)
  'Condukt Business Verification',
  'Condukt Ownership Values',
  'Condukt UBO Detect',
  // CreditSafe (Business Identity & Credit Data)
  'Creditsafe Connect - Companies',
  // Enigma (Business Identity & Credit Data)
  'Enigma',
  'Enigma KYB',
  // Kyckr (Business Identity & Credit Data)
  'Kyckr',
  'Kyckr Registry Document',
  // Middesk (Business Identity & Credit Data)
  'Middesk',
  'Middesk Documents',
  'Middesk Signals',
  // OpenCorporates (Business Identity & Credit Data)
  'OpenCorporates',
  // Refinitiv (Business Identity & Credit Data)
  'GDC Worldview',
  // TrueBiz (Business Identity & Credit Data)
  'TrueBiz Company Lookup',
  // Trulioo (Business Identity & Credit Data)
  'Trulioo KYB',
  'Trulioo Platform Business Insights',
] as const;

/**
 * Check if any credit vendor service is configured in the workflow.
 * Used to conditionally render Credit Panel card.
 */
export const hasCreditServiceConfigured = (evaluation: Evaluation): boolean => {
  const services = evaluation.final_response?.summary?.services;
  if (!services) return false;

  const configuredServices = Object.keys(services);
  return CREDIT_VENDOR_NAMES.some(vendor => configuredServices.includes(vendor));
};

/**
 * Check if Diligence card has data (excluding watchlist section)
 *
 * The Diligence panel has subsections:
 * - Fraud: diligence.fraud.sources (fraud scores from various services)
 * - Financial: diligence.financial.credit (credit/financial reports)
 * - Identity Questions: diligence.identity_questions
 *
 * Note: Watchlist section is handled separately via hasWatchlistServiceConfigured
 * since its visibility depends on whether watchlist vendors are configured.
 *
 * Returns true if any non-watchlist subsection has meaningful data.
 */
export const hasDiligenceDataExcludingWatchlist = (evaluation: Evaluation): boolean => {
  const { diligence } = evaluation.final_response;

  // Check fraud scores (diligence.fraud.sources)
  const hasFraudSources = !!(
    diligence.fraud?.sources && Object.keys(diligence.fraud.sources).length > 0
  );

  // Check financial/credit data (diligence.financial.credit)
  const hasFinancialData = !!(
    diligence.financial?.credit
    && Array.isArray(diligence.financial.credit)
    && diligence.financial.credit.length > 0
  );

  // Check identity questions (diligence.identity_questions)
  const hasIdentityQuestions = !!(
    diligence.identity_questions
    && Array.isArray(diligence.identity_questions)
    && diligence.identity_questions.length > 0
  );

  return hasFraudSources || hasFinancialData || hasIdentityQuestions;
};
